'use client';

import * as React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Script from 'next/script';
import { useTranslations, useLocale } from 'next-intl';
import { useTheme } from 'next-themes';
import { Facebook, Instagram, Youtube, Linkedin, Send, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getLocalizedPath, getLocalizedCountryPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { getSlugFromFeature } from '@/lib/feature-slugs';
import { getSlugFromPage } from '@/lib/page-slugs';
import { Typography } from '../ui/typography';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'bpract-chatbot': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'chatbot-id'?: string;
        'is-popup'?: string;
      };
    }
  }
}

interface PlanMenuItem {
  title: string;
  slug: string;
  url?: string;
}

export function Footer() {
  const t = useTranslations('footer');
  const locale = useLocale();
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [planMenuItems, setPlanMenuItems] = React.useState<PlanMenuItem[]>([]);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  // Fetch plans from backend for footer
  React.useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch(`/api/mlm-plans/menu?locale=${locale}`, { cache: 'no-store' });
        if (res.ok) {
          const plans: PlanMenuItem[] = await res.json();
          // Use translated title and url if available, fallback to slug
          setPlanMenuItems(
            plans.slice(0, 6).map(plan => ({
              title: plan.title,
              slug: plan.slug,
              url: plan.url || getLocalizedPath(plan.slug, locale),
            }))
          );
        }
      } catch (error) {
        setPlanMenuItems([]);
      }
    };
    fetchPlans();
  }, [locale]);

  React.useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 300);
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Inject aria-labels into the third-party bpract-chatbot widget buttons.
  // The widget renders inside Shadow DOM and ships without accessible names,
  // failing Lighthouse's "Buttons do not have an accessible name" audit.
  React.useEffect(() => {
    const widget = document.querySelector<HTMLElement>('bpract-chatbot');
    if (!widget) return;

    const apply = (root: ShadowRoot) => {
      root.querySelectorAll<HTMLButtonElement>('button.launcher').forEach(btn => {
        if (!btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', 'Open chat');
      });
      root.querySelectorAll<HTMLButtonElement>('button.header-btn').forEach(btn => {
        if (!btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', 'Close chat');
      });
    };

    let observer: MutationObserver | undefined;
    const intervalId = window.setInterval(() => {
      const root = widget.shadowRoot;
      if (!root) return;
      window.clearInterval(intervalId);
      apply(root);
      observer = new MutationObserver(() => apply(root));
      observer.observe(root, { childList: true, subtree: true });
    }, 300);

    const timeoutId = window.setTimeout(() => window.clearInterval(intervalId), 30000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative mt-[150px]">
      <div className="bg-[#0f0f0f] pt-20">
        <div className="px-4 mx-auto container">
          <div className="relative  bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 mx-auto rounded-2xl mt-[-200px]" >
            {/* Star/Speckle effect */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: '40px 40px'
              }} />
            </div>

            <div className="container mx-auto px-4 py-10 relative z-10 ">
              <div className="max-w-4xl mx-auto text-center">
                <Typography variant="h5" className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {t('ctaTitle')}
                </Typography>
                <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  {t('ctaDescription')}
                </p>
                <div className="flex flex-row gap-4 justify-center">
                  <Button
                    href={getLocalizedPagePath('contact', locale)}
                    variant="primary"
                    size="md"
                    showArrow={false}
                    className="!bg-white !text-blue-600 hover:!bg-gray-100 !border-white !text-center"
                  >
                    {t('buyNow')}
                  </Button>
                  <Button
                    href={getLocalizedPagePath('free-mlm-software-demo', locale)}
                    variant="outline"
                    size="md"
                    showArrow={false}
                    className="!border-2 !border-white !text-white hover:!bg-white/10"
                  >
                    {t('tryDemo')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Footer Content */}
        <div className="container mx-auto px-4 py-10 md:py-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-12 mb-12">
            {/* Company */}
            <div>
              <h6 className="text-base font-semibold text-white mb-4">
                {t('company')}
              </h6>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    href={getLocalizedPagePath('about', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('aboutUs')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('contact', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('contactUs')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('mlm-software-features', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('allSoftwareFeatures')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('mlm-software-plans', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('allMlmPlans')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('free-mlm-software-demo', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('allMlmSoftwareDemo')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('mlm-software-modules', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('allMlmSoftwareModules')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('mlm-software-services', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('allMlmSoftwareServices')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* MLM Plans */}
            <div>
              <h6 className="text-base font-semibold text-white mb-4">
                {t('mlmPlans')}
              </h6>
              <ul className="space-y-2.5">
                {planMenuItems.length > 0 ? (
                  planMenuItems.map((plan, index) => (
                    <li key={plan.slug || `plan-${index}`}>
                      <Link
                        href={plan.url || getLocalizedPath(plan.slug, locale)}
                        className="text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        {plan.title}
                      </Link>
                    </li>
                  ))
                ) : (
                  <li>
                    <Link
                      href={getLocalizedPagePath('mlm-software-plans', locale)}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {t('viewAllPlans')}
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Integrations */}
            <div>
              <h6 className="text-base font-semibold text-white mb-4">
                {t('integrations') || 'Integrations'}
              </h6>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    href={getLocalizedPath(`${getSlugFromPage('mlm-software-features', locale) || 'mlm-software-features'}/${getSlugFromFeature('bitcoin-integration', locale) || 'bitcoin-integration'}`, locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('bitcoinIntegration')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPath(`${getSlugFromPage('mlm-software-features', locale) || 'mlm-software-features'}/${getSlugFromFeature('sms-integration', locale) || 'sms-integration'}`, locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('smsIntegration')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPath(`${getSlugFromPage('mlm-software-features', locale) || 'mlm-software-features'}/${getSlugFromFeature('e-pin', locale) || 'e-pin'}`, locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('epin')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPath(`${getSlugFromPage('mlm-software-features', locale) || 'mlm-software-features'}/${getSlugFromFeature('e-wallet', locale) || 'e-wallet'}`, locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('eWallet')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPath(`${getSlugFromPage('mlm-software-features', locale) || 'mlm-software-features'}/${getSlugFromFeature('e-mail-system', locale) || 'e-mail-system'}`, locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('emailSystem')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h6 className="text-base font-semibold text-white mb-4">
                {t('resources') || 'Resources'}
              </h6>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    href={getLocalizedPagePath('blog', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('blog')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('faq', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('faq')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('testimonials', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('testimonials')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('industry-solutions', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('industries')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('changelog', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('changelog')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPagePath('pricing', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('pricing')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPath('/mlm-calculator', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('mlmCalculator')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Available Countries */}
            <div>
              <h6 className="text-base font-semibold text-white mb-4">
                {t('availableCountries')}
              </h6>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    href={getLocalizedCountryPath('italy', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('italy')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('germany', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('germany')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('denmark', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('denmark')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('malaysia', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('malaysia')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('singapore', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('singapore')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('us', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('us')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('spain', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('spain')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('france', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('france')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedCountryPath('unitedKingdom', locale)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {t('unitedKingdom')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Social Media Icons */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="flex-1 h-px bg-gray-800"></div>
            <div className="flex gap-3">
              <Link
                href="https://www.facebook.com/businessmlmsoftware"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </Link>
              <Link
                href="https://www.instagram.com/businessmlmsoftware/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </Link>
              <Link
                href="https://x.com/businessmlmsof1"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                aria-label="X (Twitter)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </Link>
              <Link
                href="https://www.youtube.com/channel/UCrAh5BZ2CZFLqADjEwRhGfg"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </Link>
              <Link
                href="https://www.linkedin.com/company/business-mlm-software/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </Link>
              <Link
                href="https://api.whatsapp.com/send/?phone=%2B918590137115&text&type=phone_number&app_absent=0"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                aria-label="WhatsApp"
              >
                <Send className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex-1 h-px bg-gray-800"></div>
          </div>

          {/* Logo and Description */}
          <div className="text-center mb-4">
            <Link href={getLocalizedPath('/', locale)} className="inline-block mb-6">
              <span className="relative block w-[200px] h-[48px]">
                <Image
                  src={mounted && resolvedTheme === 'dark' ? '/images/common/business-logo-white.png' : '/images/common/business-logo.webp'}
                  alt="Business MLM Software"
                  fill
                  className="object-contain"
                  loading="lazy"
                  quality={70}
                  sizes="200px"
                />
              </span>
            </Link>
            <p className="text-sm text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {t('description')}
            </p>
          </div>

          {/* Copyright and Legal Links */}
          <div className="pt-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-sm text-gray-400">
              <p className='text-center md:text-left'>
                {t('copyright').split('Bpract Software Solutions')[0]}
                <Link
                  href="https://bpract.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors underline"
                >
                  Bpract Software Solutions
                </Link>
                {t('copyright').split('Bpract Software Solutions')[1]}
              </p>
              <div className="hidden md:block">|</div>
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
                <Link
                  href={getLocalizedPagePath('privacy', locale)}
                  className="hover:text-white transition-colors"
                >
                  {t('privacyPolicy')}
                </Link>
                <span>|</span>
                <Link
                  href={getLocalizedPagePath('refund-policy', locale)}
                  className="hover:text-white transition-colors"
                >
                  {t('refundPolicy')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bpract Chatbot Widget - production only; widget host CORS doesn't allow localhost */}
      {process.env.NODE_ENV === 'production' && (
        <>
          <Script
            src="https://kaag.ai/widget/loader.js"
            strategy="lazyOnload"
          />
          <bpract-chatbot chatbot-id="businessmlm" is-popup="true"></bpract-chatbot>
        </>
      )}

      {/* Go to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          style={{ bottom: '4.2rem' }}
          className="fixed right-8 z-50 w-12 h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
        </button>
      )}
    </footer>
  );
}
